import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SettingsProvider } from "./contexts/SettingsContext";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Items from "./pages/Items";
import Categories from "./pages/Categories";
import StorageLocations from "./pages/StorageLocations";
import Users from "./pages/Users";
import CheckInOutHistory from "./pages/CheckInOutHistory";
import Settings from "./pages/Settings";
import Scanner from "./pages/Scanner";
import ItemDetail from "./pages/ItemDetail";
import Props from "./pages/Props";
import Containers from "./pages/Containers";
import ContainerDetail from "./pages/ContainerDetail";
import PropCategories from "./pages/PropCategories";
import Costumes from "./pages/Costumes";
import Wardrobes from "./pages/Wardrobes";
import WardrobeDetail from "./pages/WardrobeDetail";
import CostumeCategories from "./pages/CostumeCategories";

// Components
import Sidebar from "./components/Sidebar";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/items" replace />;
  }

  return children;
};

// Layout with Sidebar
const DashboardLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="md:ml-64 min-h-screen">
        <div className="p-6 md:p-8 pt-16 md:pt-8">
          {children}
        </div>
      </main>
    </div>
  );
};

// Viewer Route - redirects viewers to items page
const ViewerAwareRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (user?.role === 'viewer') {
    return <Navigate to="/items" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} 
      />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ViewerAwareRoute>
                <Dashboard />
              </ViewerAwareRoute>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/items"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Items />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/categories"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <DashboardLayout>
              <Categories />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/locations"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <DashboardLayout>
              <StorageLocations />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/history"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <DashboardLayout>
              <CheckInOutHistory />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardLayout>
              <Users />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardLayout>
              <Settings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/scanner"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Scanner />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Public item detail page - accessible via QR scan */}
      <Route
        path="/item/:id"
        element={<ItemDetail />}
      />
      
      {/* Props section */}
      <Route
        path="/props"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Props />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/containers"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Containers />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/prop-categories"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <DashboardLayout>
              <PropCategories />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Public container detail page */}
      <Route
        path="/container/:id"
        element={<ContainerDetail />}
      />
      
      {/* Costumes section */}
      <Route
        path="/costumes"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Costumes />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/wardrobes"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Wardrobes />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/costume-categories"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <DashboardLayout>
              <CostumeCategories />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Public wardrobe detail page */}
      <Route
        path="/wardrobe/:id"
        element={<WardrobeDetail />}
      />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <SettingsProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
}

export default App;
